// Fix FV (2026-10-13): "con los mismos filtros de compras de materiales también por favor" (Sergio, 13/10).
// Todas las pestañas de Servicios/Alquileres llevan la barra de Materiales: Cliente → Obra →
// Presupuesto (compartido entre pestañas, como en Materiales), Buscar, Estado, Urgencia y "Limpiar".
// Solo filtra lo que se muestra: no cambia datos ni cálculos.
import { useState } from "react";
import { X } from "lucide-react";
import { FiltroClienteObraPresupuesto, matchClienteObraPresupuesto } from "./FiltroClienteObraPresupuesto";
import { BusquedaEstadoUrgenciaFilters } from "./BusquedaFilters";

// Dónde busca "Buscar": números de los documentos, obra, cliente, presupuesto, proveedor, solicitante e ítems.
const textoDoc = (d) => !d ? "" : [
  d.number, d.ps_number, d.pps_number, d.aps_number, d.ots_number, d.project_name, d.lista_obra_nombre,
  d.lista_cliente_nombre, d.obra_rapida_nombre, d.presupuesto_codigo, d.provider_name, d.requested_by_name,
  ...(d.provider_columns || []), ...[...(d.items || []), ...(d.analysis_items || [])].map(i => i.description || i.item_name),
].filter(Boolean).join(" ").toLowerCase();

const etiqueta = (k, etiquetas) => etiquetas[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1).replace(/_/g, " ") : "—");
const porDocumento = (d) => d;
const porStatus = (d) => d.status;

// `ref`: el documento que tiene la obra y el presupuesto (por ejemplo, la OTS de una certificación).
// `estado`: lo que filtra "Estado" en esa lista. `sinEstado`: listas de "pendientes de generar" que no se filtran por estado.
export function useFiltrosServicios({ listasClientes = [], listasObras = [], filterCliente = "", filterObra = "", filterPresupuesto = "" } = {}) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("todos");
  const [filterUrgency, setFilterUrgency] = useState("");
  const criterios = { filterCliente, filterObra, filterPresupuesto };
  const catalogos = { listasClientes, listasObras };
  const enObra = (d, ref) => matchClienteObraPresupuesto(ref(d) || {}, criterios, catalogos);
  const filtrar = (lista, { ref = porDocumento, estado = porStatus, sinEstado = false } = {}) => {
    const s = search.trim().toLowerCase();
    return (lista || []).filter(d => {
      if (!enObra(d, ref)) return false;
      if (!sinEstado && filterStatus !== "todos" && estado(d) !== filterStatus) return false;
      if (filterUrgency && (d.urgency || ref(d)?.urgency || "normal") !== filterUrgency) return false;
      if (s && !`${textoDoc(d)} ${textoDoc(ref(d))}`.includes(s)) return false;
      return true;
    });
  };
  // Opciones de "Estado" con su cantidad (con el filtro Cliente → Obra → Presupuesto aplicado, como en Materiales).
  // `fijos`: los estados que siempre se ofrecen, aunque estén en 0; los demás aparecen si hay alguno.
  const opciones = (lista, { ref = porDocumento, estado = porStatus, etiquetas = {}, fijos = [] } = {}) => {
    const base = (lista || []).filter(d => enObra(d, ref));
    const cuenta = {};
    fijos.forEach(k => { cuenta[k] = 0; });
    base.forEach(d => { const k = estado(d); cuenta[k] = (cuenta[k] || 0) + 1; });
    if (filterStatus !== "todos" && !(filterStatus in cuenta)) cuenta[filterStatus] = 0;
    const orden = [...fijos, ...Object.keys(etiquetas)];
    const pos = (k) => (orden.indexOf(k) + 1) || 99;
    const claves = Object.keys(cuenta).sort((a, b) => pos(a) - pos(b));
    return [
      { value: "todos", label: `Todos (${base.length})` },
      ...claves.map(k => ({ value: k, label: `${etiqueta(k, etiquetas)} (${cuenta[k]})` })),
    ];
  };
  const activo = !!(filterCliente || filterObra || filterPresupuesto || search || filterUrgency || filterStatus !== "todos");
  return { search, setSearch, filterStatus, setFilterStatus, filterUrgency, setFilterUrgency, filtrar, opciones, activo };
}

// La barra, igual a la de Materiales. `children` va a la derecha (por ejemplo, "Nuevo Pedido").
export function BarraFiltrosServicios({ fl, filtros = {}, statusOptions = [], searchPlaceholder = "Buscar...", testIdPrefix = "svc", children }) {
  const nada = () => {};
  const { listasClientes = [], listasObras = [], allBudgets = [], filterCliente = "", setFilterCliente = nada,
    filterObra = "", setFilterObra = nada, filterPresupuesto = "", setFilterPresupuesto = nada } = filtros;
  const limpiar = () => {
    setFilterCliente(""); setFilterObra(""); setFilterPresupuesto("");
    fl.setSearch(""); fl.setFilterUrgency(""); fl.setFilterStatus("todos");
  };
  return (
    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between" data-testid={`${testIdPrefix}-filtros`}>
      <div className="flex items-center gap-2 flex-wrap">
        <FiltroClienteObraPresupuesto
          listasClientes={listasClientes} listasObras={listasObras} allBudgets={allBudgets}
          filterCliente={filterCliente} setFilterCliente={setFilterCliente}
          filterObra={filterObra} setFilterObra={setFilterObra}
          filterPresupuesto={filterPresupuesto} setFilterPresupuesto={setFilterPresupuesto}
          testIdPrefix={testIdPrefix}
        />
        <BusquedaEstadoUrgenciaFilters
          search={fl.search} setSearch={fl.setSearch} searchPlaceholder={searchPlaceholder}
          filterStatus={fl.filterStatus} setFilterStatus={fl.setFilterStatus} statusOptions={statusOptions}
          filterUrgency={fl.filterUrgency} setFilterUrgency={fl.setFilterUrgency}
          testIdPrefix={testIdPrefix}
        />
        {fl.activo && (
          <button onClick={limpiar}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded-xl px-2 py-2 hover:bg-gray-50 transition-all" title="Limpiar filtros"
            data-testid={`${testIdPrefix}-clear-filter`}>
            <X size={12} /> Limpiar
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
